import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}/* =========================================
   ANNA'S DUBLIN
   MOBILE INFINITE PRODUCT GALLERY
   ========================================= */

function initAnnaInfiniteProductGallery() { return;
  if (!window.matchMedia('(max-width: 749px)').matches) return;

  document
    .querySelectorAll('.media-gallery--hint slideshow-slides')
    .forEach((scroller) => {
      if (scroller.dataset.annaInfiniteReady === 'true') return;

      const realSlides = Array.from(
        scroller.querySelectorAll(
          ':scope > slideshow-slide:not([data-anna-gallery-clone])'
        )
      );

      if (realSlides.length < 2) return;

      scroller.dataset.annaInfiniteReady = 'true';

      const firstSlide = realSlides[0];
      const lastSlide = realSlides[realSlides.length - 1];

      const firstClone = firstSlide.cloneNode(true);
      const lastClone = lastSlide.cloneNode(true);

      firstClone.setAttribute('data-anna-gallery-clone', 'first');
      lastClone.setAttribute('data-anna-gallery-clone', 'last');

      firstClone.removeAttribute('ref');
      lastClone.removeAttribute('ref');

      firstClone.setAttribute('aria-hidden', 'true');
      lastClone.setAttribute('aria-hidden', 'true');

      scroller.insertBefore(lastClone, firstSlide);
      scroller.appendChild(firstClone);

      const centerSlide = (slide, behavior = 'auto') => {
        const left =
          slide.offsetLeft -
          (scroller.clientWidth - slide.offsetWidth) / 2;

        scroller.scrollTo({
          left,
          behavior,
        });
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          centerSlide(firstSlide);
        });
      });

      let scrollTimer;

      scroller.addEventListener(
        'scroll',
        () => {
          clearTimeout(scrollTimer);

          scrollTimer = setTimeout(() => {
            const scrollerCenter =
              scroller.scrollLeft + scroller.clientWidth / 2;

            const firstCloneCenter =
              firstClone.offsetLeft + firstClone.offsetWidth / 2;

            const lastCloneCenter =
              lastClone.offsetLeft + lastClone.offsetWidth / 2;

            const tolerance = firstClone.offsetWidth * 0.35;

            if (
              Math.abs(scrollerCenter - firstCloneCenter) <
              tolerance
            ) {
              centerSlide(lastSlide);
              return;
            }

            if (
              Math.abs(scrollerCenter - lastCloneCenter) <
              tolerance
            ) {
              centerSlide(firstSlide);
            }
          }, 80);
        },
        { passive: true }
      );
    });
}

document.addEventListener(
  'DOMContentLoaded',
  initAnnaInfiniteProductGallery
);

window.addEventListener(
  'load',
  initAnnaInfiniteProductGallery
);

const annaGalleryObserver = new MutationObserver(() => {
  initAnnaInfiniteProductGallery();
});

annaGalleryObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
});
